import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useRetireManufacturer } from "@/api/manufacturer-tracking";
import type { components } from "@/api/schema";
import { ActionPopover } from "@/components/action-popover";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { getApiErrorMessage } from "@/lib/api-error";
import { parseUTCDate } from "@/lib/utils";

type Retirement = components["schemas"]["ManufacturerRetirementRead"];

function products(count: number): string {
  return `${count.toLocaleString()} ${count === 1 ? "product" : "products"}`;
}

/**
 * Retire a manufacturer we can no longer sell, such as one that went out of business (USA-1445):
 * every product is discontinued, so the store stops selling it and quotes skip it.
 */
export function RetirePanel({
  manufacturerUuid,
  manufacturerName,
  retirement,
  activeProductCount,
}: {
  manufacturerUuid: string;
  manufacturerName: string;
  retirement: Retirement | null;
  activeProductCount: number;
}) {
  const retire = useRetireManufacturer(manufacturerUuid);

  function confirm(close: () => void) {
    retire.mutate(undefined, {
      onSuccess: () => {
        close();
        toast.success(`${manufacturerName} retired`);
      },
      onError: (error) =>
        toast.error(getApiErrorMessage(error, `Could not retire ${manufacturerName}`)),
    });
  }

  return (
    <section
      aria-labelledby="mfr-retirement"
      className="overflow-hidden rounded-xl border bg-card"
    >
      <div className="border-b px-4 py-2">
        <h2 id="mfr-retirement" className="text-sm font-semibold">
          Retirement
        </h2>
      </div>
      {retirement ? (
        <div
          aria-live="polite"
          className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-3 text-sm"
        >
          <Badge variant="destructive">Retired</Badge>
          <span className="text-muted-foreground">
            {parseUTCDate(retirement.retired_at).toLocaleDateString()}
            {retirement.retired_by_name && ` by ${retirement.retired_by_name}`}
          </span>
          {activeProductCount > 0 && (
            <span className="inline-flex items-center gap-1.5 text-muted-foreground">
              <Loader2 aria-hidden="true" className="size-3.5 animate-spin" />
              Discontinuing {products(activeProductCount)}
            </span>
          )}
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
          <span className="text-sm text-muted-foreground">
            {products(activeProductCount)} for sale
          </span>
          <ActionPopover
            align="end"
            trigger={
              <Button variant="destructive" size="sm">
                Retire {manufacturerName}
              </Button>
            }
            title={`Retire ${manufacturerName}?`}
            confirmLabel="Retire"
            confirmVariant="destructive"
            pending={retire.isPending}
            pendingLabel="Retiring..."
            onConfirm={(_note, close) => confirm(close)}
          >
            <p className="text-xs text-muted-foreground">
              All {products(activeProductCount)} will show as Discontinued on the store and
              can no longer be ordered or quoted. Its brand page leaves the store and sends
              visitors to the category most of its products are in. Quoting and catalog updates
              stop for {manufacturerName}.
            </p>
          </ActionPopover>
        </div>
      )}
    </section>
  );
}
